import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Participant } from '../../../../../../../../core/types/common.types';
import { ParticipantAction } from '../ParticipantActionMenu';

interface ParticipantActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formAction: ParticipantAction | null;
  participant: Participant;
}

function getDialogTitle(formAction: ParticipantAction | null) {
  switch (formAction) {
    case 'change-identifier':
      return 'Change Participant Identifier';

    case 'change-status':
      return 'Change Participant Status';

    case 'withdraw':
      return 'Withdraw Participant';

    case 'change-site':
      return 'Change Enrolment Site';

    default:
      return 'Participant Action';
  }
}

function getDialogDescription(formAction: ParticipantAction | null) {
  switch (formAction) {
    case 'change-identifier':
      return 'Update one or more participant identifiers, such as name, date of birth, sex at birth or NHS number. Add a reason so the change can be reviewed later.';

    case 'change-status':
      return 'Change the participant status and record the reason for the update.';

    case 'withdraw':
      return 'Record the participant withdrawal details. This should only be used where the participant has withdrawn consent.';

    case 'change-site':
      return 'Move the participant to a different enrolment site and record why the change is required.';

    default:
      return 'Add a note or confirm the participant action.';
  }
}

function renderActionFields(formAction: ParticipantAction | null, participant: Participant) {
  switch (formAction) {
    case 'change-identifier':
      return (
        <>
          <TextField
            margin="dense"
            id="nhs-number"
            name="nhsNumber"
            label="NHS Number"
            defaultValue={participant.nhsNumber ?? ''}
            fullWidth
            variant="standard"
          />

          <TextField
            select
            margin="dense"
            id="sex-at-birth"
            name="sexAtBirth"
            label="Sex at Birth"
            defaultValue={participant.sexAtBirth ?? ''}
            fullWidth
            variant="standard"
          >
            <MenuItem value="">Select</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Intersex">Intersex</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            id="dob"
            name="dob"
            label="Date of Birth"
            type="date"
            defaultValue={participant.dob ?? ''}
            fullWidth
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            margin="dense"
            id="identifier-change-reason"
            name="reason"
            label="Reason for identifier change"
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            required
          />
        </>
      );

    case 'change-status':
      return (
        <>
          <TextField
            autoFocus
            select
            margin="dense"
            id="participant-status"
            name="participantStatus"
            label="New Participant Status"
            defaultValue={participant.participantStatus ?? ''}
            fullWidth
            variant="standard"
          >
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
            <MenuItem value="DECEASED">Deceased</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            id="status-change-reason"
            name="reason"
            label="Reason for status change"
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            required
          />
        </>
      );

    case 'withdraw':
      return (
        <>
          <TextField
            autoFocus
            select
            margin="dense"
            id="withdrawal-level"
            name="withdrawalLevel"
            label="Withdrawal Level"
            defaultValue={participant.withdrawalLevel ?? ''}
            fullWidth
            variant="standard"
          >
            <MenuItem value="">Select withdrawal level</MenuItem>
            <MenuItem value="Complete">Complete Withdrawal</MenuItem>
            <MenuItem value="CollectionOnly">Sharing only</MenuItem>
            <MenuItem value="CollectionOnly">Collection Only</MenuItem>
            <MenuItem value="SharingAndCollection">Sharing and Collection</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            id="withdrawal-reason"
            name="withdrawalReason"
            label="Withdrawal Reason"
            defaultValue={participant.withdrawalReason ?? ''}
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            required
          />
        </>
      );

    case 'change-site':
      return (
        <>
          <TextField
            autoFocus
            margin="dense"
            id="ods-code"
            name="odsCode"
            label="New ODS Code"
            defaultValue={participant.site?.odsCode ?? ''}
            fullWidth
            variant="standard"
          />

          <TextField
            margin="dense"
            id="site-change-reason"
            name="reason"
            label="Reason for site change"
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            required
          />
        </>
      );

    default:
      return (
        <TextField
          autoFocus
          margin="dense"
          id="participant-action-note"
          name="participantActionNote"
          label="Action note"
          fullWidth
          multiline
          minRows={3}
          variant="standard"
        />
      );
  }
}

function ParticipantActionDialog({ isOpen, onClose, formAction, participant }: ParticipantActionDialogProps) {
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const values = Object.fromEntries(formData.entries());

    console.log({
      action: formAction,
      values,
    });

    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{getDialogTitle(formAction)}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{getDialogDescription(formAction)}</DialogContentText>

        <Box component="form" key={formAction} id="participant-action-form" onSubmit={handleSubmit}>
          {renderActionFields(formAction, participant)}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button type="submit" form="participant-action-form" variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ParticipantActionDialog;
